



















































































import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
  useRoute,
  useRouter
} from '~/utils/nuxt3-migration'
import { useSnackbar } from '~/compositions/snackbar'
import { useI18n } from '~/compositions/i18n'
import OfferAmountInput from '~/components/shared/classified/offers/OfferAmountInput.vue'
import ReceiptOrInvoiceModal from '~/components/shared/account/payments/subscriptions/ReceiptOrInvoiceModal.vue'
import { StripeSession } from '~/models/payments/stripe'
import { ButtonSize, ButtonVariant } from '~/models/app/button'

export default defineComponent({
  components: { ReceiptOrInvoiceModal, OfferAmountInput },
  props: {
    size: {
      type: String as PropType<ButtonSize>,
      default: 'md',
      required: false
    },
    clickCallback: {
      type: Function,
      required: false,
      default: null
    },
    label: {
      type: String,
      required: false,
      default: ''
    },
    icon: { type: Object, default: () => null },
    topUpFunction: {
      type: Function as PropType<
        (amount: number, requestInvoice: boolean) => Promise<StripeSession>
      >,
      required: true
    },
    variant: {
      type: String as PropType<ButtonVariant>,
      default: 'primary'
    },
    termsLink: {
      type: String,
      required: false,
      default: ''
    },
    termsLabel: {
      type: String,
      default: ''
    },
    linkMode: {
      type: Boolean,
      default: false
    },
    outline: {
      type: Boolean,
      default: false
    },
    tertiary: {
      type: Boolean,
      default: false
    },
    topUpAmounts: {
      type: Array as PropType<number[]>,
      default: () => []
    },
    defaultAmount: {
      type: Number,
      default: 10
    }
  },
  setup(props, { emit }) {
    const { clickCallback } = toRefs(props)
    const amount = ref(props.defaultAmount)
    const issueInvoice = ref(false)
    const loading = ref(false)
    const showTopUpModal = ref(false)
    const error = ref(null)
    const showReceiptModal = ref(false)

    const snackbar = useSnackbar()
    const { t } = useI18n()
    const route = useRoute()
    const router = useRouter()

    onMounted(() => {
      if (route.value.query?.triggerTopUp) {
        showTopUpModal.value = true
        router.replace({
          query: { ...route.value.query, triggerTopUp: undefined }
        })
      }
      if (route.value.query?.success) {
        snackbar.success(t('ads_top_up_success'))
        emit('success')
        router.replace({ query: { success: undefined } })
      }
    })

    const inputDisabled = computed(() => {
      if (loading.value) {
        return true
      }

      if (!props.topUpAmounts?.length) {
        return amount.value < props.defaultAmount
      }
    })

    async function onSubmit(invoice: boolean) {
      issueInvoice.value = invoice
      try {
        loading.value = true
        error.value = null
        const stripeSession = await props.topUpFunction(
          Number(amount.value),
          issueInvoice.value
        )
        window.location = stripeSession.url
      } catch (error) {
        if (error.response?.data?.data?.errors?.name) {
          error.value = error.response?.data?.data?.errors?.name
        } else {
          snackbar.error(
            error.response?.data?.error || t('something went wrong')
          )
        }
      } finally {
        showReceiptModal.value = false
        loading.value = false
      }
    }

    async function onAddClick() {
      if (clickCallback.value) {
        try {
          await clickCallback.value()
        } catch (_e) {
          return
        }
      }
      showTopUpModal.value = true
      emit('add-clicked')
    }

    function resetValues() {
      if (!showReceiptModal.value) {
        amount.value = props.defaultAmount
        issueInvoice.value = false
      }
    }

    function goNext() {
      showReceiptModal.value = true
      showTopUpModal.value = false
    }

    function onReceiptModalHide() {
      if (showReceiptModal.value) {
        showReceiptModal.value = false
        amount.value = props.defaultAmount
      }
    }

    function onReceiptBack() {
      showReceiptModal.value = false
    }

    function onTopUpAmountButtonClick(topUpAmount: number) {
      amount.value = topUpAmount
    }

    function topUpAmountIsSelected(topUpAmount: number) {
      return amount.value === topUpAmount
    }

    return {
      loading,
      amount,
      showTopUpModal,
      error,
      issueInvoice,
      showReceiptModal,
      inputDisabled,
      t,
      onSubmit,
      goNext,
      onReceiptModalHide,
      onReceiptBack,
      onTopUpAmountButtonClick,
      topUpAmountIsSelected,
      onAddClick,
      resetValues
    }
  }
})
